import { useEffect, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, fmt } from "../../lib/api";

const CHANNEL_COLORS = { shop: "#EAB308", ebay: "#38BDF8", tiktok: "#F43F5E" };

const tooltipStyle = { background: "#121216", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 };

function StatCard({ label, value, sub, testid }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <p className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</p>
      <p className="mt-2 font-heading text-2xl font-bold text-white">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

export default function DashboardPage() {
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rankings, setRankings] = useState(null);
  const [rankTab, setRankTab] = useState("by_revenue");

  useEffect(() => {
    api.get("/admin/analytics/overview").then((r) => setOverview(r.data));
    api.get("/admin/analytics/timeseries").then((r) => setSeries(r.data.series));
    api.get("/admin/analytics/categories").then((r) => setCategories(r.data.categories));
    api.get("/admin/analytics/rankings").then((r) => setRankings(r.data));
  }, []);

  if (!overview) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />;

  const channelData = Object.entries(overview.channels).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6">
        <p className="eyebrow">Analytics</p>
        <h1 className="font-heading text-2xl font-bold text-white">Sales performance</h1>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Revenue · 30d" value={fmt(overview.revenue_30d)} sub={`${overview.growth_rate >= 0 ? "+" : ""}${overview.growth_rate}% vs prior 30d`} testid="stat-revenue" />
        <StatCard label="Orders · 30d" value={overview.orders_30d} sub={`${overview.total_orders} all time`} testid="stat-orders" />
        <StatCard label="Avg order value" value={fmt(overview.aov)} sub={`${overview.total_products} products live`} testid="stat-aov" />
        <StatCard label="Open returns" value={overview.open_returns} sub={`${overview.pending_escalations} chat escalations`} testid="stat-returns" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="card-surface p-5 lg:col-span-2" data-testid="chart-revenue-timeseries">
          <p className="mb-4 font-heading text-sm font-semibold text-white">Revenue — last 30 days</p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={series}>
              <defs>
                <linearGradient id="gold" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#EAB308" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#EAB308" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} stroke="rgba(255,255,255,0.1)" />
              <YAxis tick={{ fill: "#64748B", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" width={50} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmt(v), "Revenue"]} />
              <Area type="monotone" dataKey="revenue" stroke="#EAB308" strokeWidth={2} fill="url(#gold)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="card-surface p-5" data-testid="chart-channel-split">
          <p className="mb-4 font-heading text-sm font-semibold text-white">Orders by channel</p>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={channelData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} strokeWidth={0}>
                {channelData.map((c) => <Cell key={c.name} fill={CHANNEL_COLORS[c.name] || "#94A3B8"} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 flex justify-center gap-4 text-xs text-slate-400">
            {channelData.map((c) => (
              <span key={c.name} className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ background: CHANNEL_COLORS[c.name] }} />
                {c.name} ({c.value})
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="card-surface p-5" data-testid="chart-category-revenue">
          <p className="mb-4 font-heading text-sm font-semibold text-white">Revenue by category · 30d</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={categories} layout="vertical">
              <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
              <XAxis type="number" tick={{ fill: "#64748B", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" />
              <YAxis type="category" dataKey="category" tick={{ fill: "#94A3B8", fontSize: 11 }} stroke="rgba(255,255,255,0.1)" width={110} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmt(v), "Revenue"]} />
              <Bar dataKey="revenue_30d" fill="#EAB308" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-3 flex flex-wrap gap-3 text-xs">
            {categories.map((c) => (
              <span key={c.category} className="text-slate-400">
                {c.category}: <span className={c.growth_rate >= 0 ? "text-emerald-400" : "text-rose-400"}>{c.growth_rate >= 0 ? "+" : ""}{c.growth_rate}%</span>
              </span>
            ))}
          </div>
        </div>

        <div className="card-surface p-5" data-testid="rankings-panel">
          <div className="mb-4 flex items-center justify-between">
            <p className="font-heading text-sm font-semibold text-white">Product rankings</p>
            <div className="flex gap-1 rounded-full border border-white/10 p-1">
              {[["by_revenue", "Revenue"], ["by_units", "Orders"], ["by_growth", "Growth"]].map(([k, l]) => (
                <button
                  key={k}
                  data-testid={`rank-tab-${k}`}
                  onClick={() => setRankTab(k)}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${rankTab === k ? "bg-amber-400 text-black" : "text-slate-400"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {rankings?.[rankTab]?.slice(0, 6).map((r, i) => (
              <div key={r.product_id} className="flex items-center gap-3 rounded-xl border border-white/[0.06] p-2.5" data-testid={`rank-row-${i}`}>
                <span className="w-5 text-center font-mono text-sm text-amber-400">{i + 1}</span>
                {r.image && <img src={r.image} alt="" className="h-9 w-9 rounded-lg object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-white">{r.name}</p>
                  <p className="text-[11px] text-slate-500">{r.category}</p>
                </div>
                <p className="text-sm font-semibold text-white">
                  {rankTab === "by_revenue" && fmt(r.revenue)}
                  {rankTab === "by_units" && `${r.units} sold`}
                  {rankTab === "by_growth" && <span className="text-emerald-400">+{r.growth_rate}%</span>}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
